import { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Users, Briefcase, UserCheck, Brain, TrendingUp, Activity,
  ArrowUpRight, ArrowDownRight, Zap, Award, ChevronRight, CreditCard, Star,
  Target, GraduationCap, RefreshCw, Loader2, Bell, Layers, CheckCircle2, BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useNavigate } from 'react-router-dom';
import { useSTNAuth } from '@/context/STNAuthContext';
import { toast } from 'sonner';
import { getFounderDashboard } from '@/api/stnApi';

const ACTIVITY_META = {
  application: { icon: Users, color: 'text-chart-1', bg: 'bg-chart-1/10' },
  milestone:   { icon: Award, color: 'text-chart-3', bg: 'bg-chart-3/10' },
  billing:     { icon: CreditCard, color: 'text-chart-3', bg: 'bg-chart-3/10' },
  payment:     { icon: CreditCard, color: 'text-chart-3', bg: 'bg-chart-3/10' },
  match:       { icon: Zap, color: 'text-chart-1', bg: 'bg-chart-1/10' },
  mentor:      { icon: GraduationCap, color: 'text-chart-2', bg: 'bg-chart-2/10' },
  system:      { icon: Bell, color: 'text-muted-foreground', bg: 'bg-muted' },
  default:     { icon: Bell, color: 'text-muted-foreground', bg: 'bg-muted' },
};
const timeAgo = (d) => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};
const scoreCls = (s) => s >= 75 ? 'text-emerald-500' : s >= 50 ? 'text-amber-500' : 'text-rose-500';
const STATUS_CLS = {
  active: 'bg-emerald-500/10 text-emerald-500', enrolling: 'bg-emerald-500/10 text-emerald-500',
  completed: 'bg-muted text-muted-foreground', draft: 'bg-muted text-muted-foreground',
};

const KPICard = ({ icon: Icon, label, value, change, changeType, color, bg, sub }) => (
  <Card className="border-border kpi-glow hover:shadow-elegant transition-shadow duration-300">
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}><Icon className={`w-5 h-5 ${color}`} /></div>
        {change != null && change !== '' && (
          <span className={`flex items-center gap-0.5 text-xs font-medium ${changeType === 'down' ? 'text-destructive' : 'text-success'}`}>
            {changeType === 'down' ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}{change}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-foreground mb-0.5">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { founder, startup } = useSTNAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getFounderDashboard()
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Could not load dashboard'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const f = data?.features || {};
  const planSlug = data?.plan?.slug || 'free';
  const company = data?.company || {};
  const firstName = (founder?.name || company.name || 'there').split(' ')[0];

  // Build KPI cards only for granted features.
  const kpis = [];
  if (f.hiring && data?.hiring) {
    kpis.push({ icon: Briefcase, label: 'Active Roles', value: data.hiring.activeRoles, color: 'text-chart-1', bg: 'bg-chart-1/10' });
    kpis.push({ icon: Users, label: 'Applications', value: data.hiring.applications, color: 'text-chart-2', bg: 'bg-chart-2/10',
      change: data.hiring.appsDelta ? `${data.hiring.appsDelta > 0 ? '+' : ''}${data.hiring.appsDelta}` : '', changeType: data.hiring.appsDelta < 0 ? 'down' : 'up', sub: 'vs last month' });
    kpis.push({ icon: UserCheck, label: 'Hired', value: data.hiring.hired, color: 'text-chart-3', bg: 'bg-chart-3/10' });
    kpis.push({ icon: Brain, label: 'Hire Rate', value: `${data.hiring.hireRate}%`, color: 'text-primary', bg: 'bg-primary/10' });
  }
  if (f.batches && data?.batches) {
    kpis.push({ icon: Layers, label: 'Partnered Batches', value: data.batches.partneredBatches, color: 'text-chart-4', bg: 'bg-chart-4/10' });
    kpis.push({ icon: GraduationCap, label: 'Interns in Training', value: data.batches.totalInterns, color: 'text-chart-1', bg: 'bg-chart-1/10', sub: `${data.batches.activated} activated` });
    kpis.push({ icon: TrendingUp, label: 'Avg Assignment', value: `${data.batches.avgAssignmentScore}%`, color: 'text-chart-3', bg: 'bg-chart-3/10', sub: `Attendance ${data.batches.avgAttendance}%` });
  }
  if (f.mentors && data?.mentors != null) {
    kpis.push({ icon: Star, label: 'Mentors', value: data.mentors, color: 'text-chart-5', bg: 'bg-chart-5/10', sub: 'onboarded' });
  }

  // Quick actions — only what they can reach.
  const actions = [];
  if (f.hiring) { actions.push({ label: 'Post Intern Role', icon: Users, path: '/interns' }); actions.push({ label: 'Browse Candidates', icon: Target, path: '/jobs' }); }
  if (f.batches) actions.push({ label: 'Assigned Batches', icon: GraduationCap, path: '/mentors?tab=assigned' });
  if (f.mentors) actions.push({ label: 'Mentors', icon: Star, path: '/mentors' });
  if (f.billing) actions.push({ label: 'Billing', icon: CreditCard, path: '/billing' });
  actions.push({ label: 'Settings', icon: Activity, path: '/settings' });

  if (loading && !data) {
    return <div className="flex items-center justify-center py-32 text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading dashboard…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {company.logoUrl && <img src={company.logoUrl} alt="" className="w-11 h-11 rounded-xl object-contain border border-border bg-white" />}
          <div>
            <h1 className="text-2xl font-bold text-foreground">Welcome back, {firstName} 👋</h1>
            <p className="text-muted-foreground text-sm mt-0.5">{company.name}{company.location ? ` — ${company.location}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="border-border" onClick={load} title="Refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          {f.batches && <Button variant="outline" size="sm" className="gap-2 border-border" onClick={() => navigate('/mentors?tab=assigned')}><BarChart3 className="w-4 h-4" /> Batch Analytics</Button>}
          {f.hiring && <Button size="sm" className="gradient-primary text-primary-foreground border-0" onClick={() => navigate('/interns')}><Briefcase className="w-4 h-4 mr-2" /> Post Role</Button>}
        </div>
      </div>

      {/* Plan banner — only when billing is on */}
      {f.billing && planSlug === 'free' && (
        <div className="flex items-center justify-between p-4 rounded-xl bg-primary/8 border border-primary/20">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-primary" />
            <div>
              <span className="text-sm font-medium text-foreground">You're on the Free Plan</span>
              <p className="text-xs text-muted-foreground">Upgrade to Plus for more capacity.</p>
            </div>
          </div>
          <Button size="sm" className="gradient-primary text-primary-foreground border-0" onClick={() => navigate('/billing')}>Upgrade</Button>
        </div>
      )}

      {/* KPI grid — adapts to however many features are granted */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {kpis.map((k, i) => <KPICard key={i} {...k} />)}
        </div>
      )}

      {/* ── Hiring section (only with hiring features) ────────────────────────── */}
      {f.hiring && data?.hiring && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-border">
            <CardHeader className="pb-0">
              <CardTitle className="text-base font-semibold text-foreground">Hiring Trend</CardTitle>
              <p className="text-xs text-muted-foreground">Applications vs Hires — last 6 months</p>
            </CardHeader>
            <CardContent className="pt-4">
              {(data.hiring.trend || []).every((t) => !t.applied && !t.hired) ? (
                <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No application activity yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={data.hiring.trend}>
                    <defs>
                      <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(263,70%,62%)" stopOpacity={0.3}/><stop offset="95%" stopColor="hsl(263,70%,62%)" stopOpacity={0}/></linearGradient>
                      <linearGradient id="gH" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(142,60%,50%)" stopOpacity={0.3}/><stop offset="95%" stopColor="hsl(142,60%,50%)" stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="applied" stroke="hsl(263,70%,62%)" strokeWidth={2} fill="url(#gA)" name="Applied" />
                    <Area type="monotone" dataKey="hired" stroke="hsl(142,60%,50%)" strokeWidth={2} fill="url(#gH)" name="Hired" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-0">
              <CardTitle className="text-base font-semibold text-foreground">Hiring Funnel</CardTitle>
              <p className="text-xs text-muted-foreground">Current pipeline</p>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {(() => { const max = Math.max(1, ...data.hiring.funnel.map((x) => x.count)); const fills = ['hsl(var(--chart-1))','hsl(var(--chart-2))','hsl(var(--chart-3))','hsl(var(--chart-4))','hsl(var(--chart-5))'];
                return data.hiring.funnel.map((item, i) => (
                  <div key={item.stage}>
                    <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">{item.stage}</span><span className="text-xs font-semibold text-foreground">{item.count}</span></div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${(item.count / max) * 100}%`, background: fills[i % fills.length] }} /></div>
                  </div>
                )); })()}
              {data.hiring.funnel.every((x) => !x.count) && <p className="text-xs text-muted-foreground py-4 text-center">No applications yet.</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Batches section (only with batch features) ────────────────────────── */}
      {f.batches && data?.batches && (
        <Card className="border-border">
          <CardHeader className="pb-0 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2"><Layers className="w-4 h-4 text-primary" /> Partnered Batches</CardTitle>
              <p className="text-xs text-muted-foreground">Training progress across batches assigned to you</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/mentors?tab=assigned')} className="text-primary text-xs gap-1">View all <ChevronRight className="w-3 h-3" /></Button>
          </CardHeader>
          <CardContent className="pt-4">
            {data.batches.list.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No batches have been assigned to you yet.</p>
            ) : (
              <div className="space-y-2">
                {data.batches.list.map((b) => (
                  <button key={b.id} onClick={() => navigate(`/assigned-batches/${b.id}`)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-muted/30 transition-colors text-left">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">{b.title}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${STATUS_CLS[String(b.status).toLowerCase()] || STATUS_CLS.draft}`}>{b.status}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{b.platform} · {b.seats} seats</p>
                    </div>
                    <div className="flex items-center gap-5 text-xs flex-shrink-0">
                      <div className="text-center"><p className="font-bold text-foreground">{b.registered}</p><p className="text-[10px] text-muted-foreground">interns</p></div>
                      <div className="text-center"><p className="font-bold text-foreground flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" />{b.activated}</p><p className="text-[10px] text-muted-foreground">active</p></div>
                      <div className="text-center"><p className={`font-bold ${scoreCls(b.avgAssignmentScore)}`}>{b.avgAssignmentScore}%</p><p className="text-[10px] text-muted-foreground">assign AI</p></div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Bottom row: activity + plan/quick actions ─────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border">
          <CardHeader className="pb-0 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold text-foreground">Recent Activity</CardTitle>
              <p className="text-xs text-muted-foreground">Latest updates{data?.unreadCount ? ` · ${data.unreadCount} unread` : ''}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')} className="text-primary text-xs gap-1">View all <ChevronRight className="w-3 h-3" /></Button>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {(data?.activity || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No recent activity.</p>
            ) : (data.activity || []).map((item, i) => {
              const meta = ACTIVITY_META[item.type] || ACTIVITY_META.default; const Icon = meta.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg}`}><Icon className={`w-4 h-4 ${meta.color}`} /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm text-foreground">{item.text}</p><p className="text-xs text-muted-foreground mt-0.5">{timeAgo(item.time)}</p></div>
                  {!item.read && <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {f.billing && data?.usage && (
            <Card className="border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground capitalize">{data?.plan?.name || planSlug} Plan Usage</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(data.usage).map(([key, u]) => (
                  <UsageRow key={key} label={USAGE_LABELS[key] || key} u={u} />
                ))}
                {planSlug === 'free' && (
                  <Button size="sm" className="w-full mt-2 gradient-primary text-primary-foreground border-0 text-xs" onClick={() => navigate('/billing')}>Upgrade for more <ArrowUpRight className="w-3 h-3 ml-1" /></Button>
                )}
              </CardContent>
            </Card>
          )}
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Quick Actions</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              {actions.map((action) => (
                <button key={action.label} onClick={() => navigate(action.path)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-muted hover:bg-accent border border-transparent hover:border-border transition-all text-center">
                  <action.icon className="w-4 h-4 text-primary" />
                  <span className="text-xs text-foreground font-medium leading-tight">{action.label}</span>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const USAGE_LABELS = { postings: 'Intern Postings', hires: 'Interns Hired (mo)', poolViews: 'Pool Views Today', teamMembers: 'Team Members' };
const UsageRow = ({ label, u }) => {
  const pct = u?.limit ? Math.min(100, Math.round((u.used / u.limit) * 100)) : (u?.used ? 100 : 0);
  const txt = u?.limit == null ? `${u?.used ?? 0} · unlimited` : `${u?.used ?? 0}/${u.limit}`;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5"><span className="text-muted-foreground">{label}</span><span className="text-foreground font-medium">{txt}</span></div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
};
